// Angular
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';

// Services
import { UserManagementService } from '@services/user-management.service';
import { CustomerService } from '@services/customer.service';

// Models
import { Manager } from '@components/account-manager';

// Constants
import { Active } from '@constants/enum';
import { CustomPaginator } from '@utils/custom-paginator';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';

// Third Party
import { finalize } from 'rxjs';

@Component({
  selector: 'app-account-manager-table',
  imports: [
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    MatSidenavModule,
    RouterLink,
    DatePipe,
    LoadingSpinnerComponent,
    FormErrorComponent,
  ],
  templateUrl: './account-manager-table.component.html',
  styleUrl: './account-manager-table.component.scss',
  providers: [{ provide: MatPaginatorIntl, useValue: CustomPaginator() }],
})
export class AccountManagerTableComponent implements OnInit {
  public userManagementService = inject(UserManagementService);
  public customerService = inject(CustomerService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  displayedColumns = ['firstName', 'lastName', 'email', 'lastLogin', 'status', 'customers', 'edit'];

  readonly defaultData = signal<Manager[]>([]);

  readonly search = signal('');
  readonly status = signal<string[]>([]);
  readonly sort = signal<Sort>({ active: 'firstName', direction: 'asc' });

  statusList: string[] = [Active.active, Active.inactive];

  readonly pageSize = signal(50);
  readonly pageIndex = signal(0);

  readonly loading = signal(true);
  readonly error = signal(false);

  readonly allCustomers = signal<Map<string, string>>(new Map());

  readonly filteredData = computed(() => {
    let data = this.defaultData();

    // Filter on status
    if (this.status().includes(Active.active) && this.status().includes(Active.inactive)) {
      // both selected — show all
    } else if (this.status().includes(Active.active)) {
      data = data.filter((element) => element.enabled == true);
    } else if (this.status().includes(Active.inactive)) {
      data = data.filter((element) => element.enabled == false);
    }

    // Check the customer list keys for possible matches
    const searchTerm = this.search().toLowerCase();
    const possibleCustomers = new Map(
      [...this.allCustomers()].filter(([key]) => key.toLowerCase().includes(searchTerm)),
    );

    // Search
    if (searchTerm) {
      data = data.filter(
        (element) =>
          element.firstName.toLowerCase().includes(searchTerm) ||
          element.lastName.toLowerCase().includes(searchTerm) ||
          element.email.toLowerCase().includes(searchTerm) ||
          element.customers.find((x) => x.includes(searchTerm)) ||
          this.searchByCustomerName(element.customers, possibleCustomers),
      );
    }

    // Sort
    return this.sortData(this.sort(), data);
  });

  readonly resultsLength = computed(() => this.filteredData().length);

  readonly sortedData = computed(() => {
    const data = this.filteredData();
    return data.slice(this.pageIndex() * this.pageSize(), (this.pageIndex() + 1) * this.pageSize());
  });

  ngOnInit() {
    this.getAllCustomers();
    this.getUsers();
  }

  getUsers() {
    this.loading.set(true);
    this.userManagementService
      .getManagerList()
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data) => this.defaultData.set(data['entity'].map((m) => this.managerMapper(m))),
        error: () => this.error.set(true),
      });
  }

  managerMapper(manager: Manager): Manager {
    return {
      ...manager,
      firstName: manager.firstName ?? '',
      lastName: manager.lastName ?? '',
      email: manager.email ?? '',
      customers: manager.customers ?? [],
    };
  }

  getAllCustomers() {
    this.customerService
      .getTenantNames()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        const map = new Map<string, string>();
        for (const [key, value] of Object.entries(data)) {
          map.set(key.toLowerCase(), value);
        }
        this.allCustomers.set(map);
      });
  }

  searchByCustomerName(customers: string[], possible: Map<string, string>) {
    for (const [, value] of possible.entries()) {
      if (customers.includes(value)) {
        return true;
      }
    }
    return false;
  }

  selectSort(sort: Sort) {
    this.sort.set(sort);
  }

  sortData(sort: Sort, dataToBeSorted: Manager[]) {
    const data = dataToBeSorted.slice();
    if (!sort.active || sort.direction === '') {
      return data;
    }
    return data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      const isDesc = sort.direction === 'desc';
      switch (sort.active) {
        case 'firstName':
          return this.compare(a.firstName.toLowerCase(), b.firstName.toLowerCase(), isAsc);
        case 'lastName':
          return this.compare(a.lastName.toLowerCase(), b.lastName.toLowerCase(), isAsc);
        case 'email':
          return this.compare(a.email.toLowerCase(), b.email.toLowerCase(), isAsc);
        case 'lastLogin':
          return this.compareLastLogin(a.lastLoginDate, b.lastLoginDate, isAsc);
        case 'status':
          return this.compare(a.enabled, b.enabled, isDesc);
        default:
          return 0;
      }
    });
  }

  compare(
    a: number | string | Date | boolean,
    b: number | string | Date | boolean,
    isAsc: boolean,
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  compareLastLogin(a: number, b: number, isAsc: boolean) {
    if (!a && !b) return 0;
    if (!a) return isAsc ? -1 : 1;
    if (!b) return isAsc ? 1 : -1;
    if (a === b) return 0;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  statusSelect(status: string[]) {
    this.status.set(status);
    this.pageIndex.set(0);
  }

  searchTable(search: string) {
    this.search.set(search);
    this.pageIndex.set(0);
  }

  handlePageEvent(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  editManager(element) {
    this.router.navigate(['/managers/edit'], { state: { kcGuid: element.guid } });
  }
}
